import { supabase } from "@/integrations/supabase/client";

export interface MasterDocumentClientInfo {
  fullName: string;
  phoneNumber: string;
  email: string;
  cityST: string;
  linkedin: string;
}

export interface MasterDocumentEntry {
  documentType: 'resume' | 'cover-letter' | 'thank-you-letter' | 'outreach-letter' | 'linkedin-profile' | 'executive-bio';
  resumeIdentifier?: string;
  jobTitle?: string;
  companyName?: string;
  recipientName?: string;
  data: Record<string, unknown>;
}

export interface MasterDocumentRequest {
  clientInfo: MasterDocumentClientInfo;
  documents: MasterDocumentEntry[];
  templateId?: 'template1' | 'template2';
}

interface GeneratedFile {
  filename: string;
  data: string; // base64
  size: number;
  documentType: string;
  jobTitle?: string;
  isRootLevel: boolean;
}

interface MasterDocumentResponse {
  success: boolean;
  files: GeneratedFile[];
  totalFiles: number;
}

export interface StoredMasterDocumentFile {
  filename: string;
  fileUrl: string;
  size: number;
  documentType: string;
}

export class MasterDocumentService {
  /**
   * Call the generate-master-documents edge function
   */
  static async generateDocuments(request: MasterDocumentRequest): Promise<MasterDocumentResponse> {
    const { data, error } = await supabase.functions.invoke('generate-master-documents', {
      body: request,
    });

    if (error) {
      console.error('Error calling generate-master-documents:', error);
      throw new Error(`Master document generation failed: ${error.message}`);
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Invalid response from master document generation');
    }

    return data as MasterDocumentResponse;
  }

  /**
   * Generate documents and store them in Supabase storage for viewer access.
   */
  static async generateAndStoreDocuments(request: MasterDocumentRequest): Promise<{ success: boolean; files: StoredMasterDocumentFile[]; totalFiles: number }> {
    const response = await this.generateDocuments(request);

    const storedFiles: StoredMasterDocumentFile[] = [];

    for (const file of response.files) {
      try {
        const byteCharacters = atob(file.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });

        const timestamp = Date.now();
        const storagePath = `master_${timestamp}_${file.filename}`;

        const { error: uploadError } = await supabase.storage
          .from('word-documents')
          .upload(storagePath, blob, { cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('word-documents')
          .getPublicUrl(storagePath);

        if (urlData?.publicUrl) {
          storedFiles.push({
            filename: file.filename,
            fileUrl: urlData.publicUrl,
            size: file.size,
            documentType: file.documentType,
          });
        }
      } catch (err) {
        console.error(`Failed to store file ${file.filename}:`, err);
      }
    }

    return { success: true, files: storedFiles, totalFiles: storedFiles.length };
  }

  /**
   * Download a single file from base64
   */
  static downloadFileFromBase64(filename: string, base64Data: string): void {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Download all generated files + uploaded files as a single organized ZIP.
   *
   * Folder structure:
   *   root/
   *     LinkedIn Profile.docx         (root-level generated docs)
   *     Executive Bio.docx            (root-level generated docs)
   *     <uploaded root files>          (pre-made LinkedIn/bio uploads)
   *     Software Engineer/
   *       Cover Letter.docx
   *       Resume.docx
   *       <uploaded resume files>
   *     Senior Developer/
   *       ...
   */
  static async downloadAsZip(
    generatedFiles: GeneratedFile[],
    zipFilename: string,
    uploadedResumeFiles?: { resumeIdentifier: string; files: File[]; jobTitle: string }[],
    folderNameOverrides?: Record<string, string>,
    uploadedRootFiles?: File[],
  ): Promise<void> {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Sanitize names so "/" or "\" inside job titles don't create unintended subfolders in the ZIP
    const sanitize = (name: string) => name.replace(/[\/\\]/g, '-').trim();

    // 1. Add generated files
    for (const file of generatedFiles) {
      const byteCharacters = atob(file.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const safeFilename = sanitize(file.filename);

      if (file.isRootLevel || !file.jobTitle) {
        zip.file(safeFilename, byteArray);
      } else {
        const rawFolder = folderNameOverrides?.[file.jobTitle] || file.jobTitle;
        const folderName = sanitize(rawFolder);
        zip.file(`${folderName}/${safeFilename}`, byteArray);
      }
    }

    // PDFs for resumes are now included directly from the edge function (Docmosis native rendering)

    if (uploadedResumeFiles && uploadedResumeFiles.length > 0) {
      for (const resume of uploadedResumeFiles) {
        const folderName = sanitize(resume.jobTitle || 'Other');
        for (const resumeFile of resume.files) {
          const arrayBuffer = await resumeFile.arrayBuffer();
          zip.file(`${folderName}/${sanitize(resumeFile.name)}`, new Uint8Array(arrayBuffer));
        }
      }
    }

    // 3. Add uploaded root-level files (pre-made LinkedIn, bios, etc.)
    if (uploadedRootFiles && uploadedRootFiles.length > 0) {
      for (const rootFile of uploadedRootFiles) {
        const arrayBuffer = await rootFile.arrayBuffer();
        zip.file(sanitize(rootFile.name), new Uint8Array(arrayBuffer));
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
